import { useState, useEffect } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import { brutoCapex, capexLlevaIvaLocal } from "../../utils/brutoCapex";
import { computarSaldoCapex } from "../../utils/capex/getSaldoCapexTarget";
import { claveTargetCapex, getSaldosCapexPorTarget } from "../../utils/capex/getSaldosCapexPorTarget";
import { generarCuotas, validarSumaCuotas } from "../../utils/cuotasUtil";
import { getPlanPagosActivos, guardarPlanPagosActivo } from "../../utils/planPagosActivos";
import { client } from "../../utils/supabase";

// Plan de pagos en cuotas de un activo CapEx planificado (menú ⋮ de la fila CapEx, 4.2.mm).
// CAJA PURA (doctrina 4.19.1 / 10.100): NO toca estado, fecha_compra ni depreciación del activo.
//
// 2c Fase 2 — máquina de estados por activo, guardada por la RPC atómica guardar_plan_pagos_activo
// (todo-o-nada en la base, 10.190/10.239). Reemplaza el Promise.all no-atómico. Tres estados:
//   · PLAN de N cuotas · FECHA ÚNICA (0 cuotas, fecha_planificada seteada) · CERO (0 cuotas, sin fecha).
// "Virgen" = ningún pago ejecutado (0 eventos vigentes en pagos_capex). En virgen se mueve libre entre
// los tres; con AL MENOS un pago está OBLIGADO a plan (no se puede eliminar ni pasar a fecha única/cero
// — la RPC lo RECHAZA). Las cuotas pagadas son intocables (candados 10.247/10.256). La RPC valida
// Σ(neto todas)=costo_unitario y gestiona activos.fecha_planificada (proyección de caja) según el estado.
//
// IVA (CapEx-IVA): el usuario ve y reparte el BRUTO — el monto real que sale por caja, idéntico
// al que muestra la fila de "Movimientos Futuros" (mapCapex/mapCuotaActivo) vía el helper único
// `brutoCapex` (reglas 10.1/10.19/10.27). PERO `plan_pagos_activos.monto_cuota` se persiste en
// NETO (contrato del sistema: mapCuotaActivo y el flujo de pago grosean el neto al usarlo). Por
// eso el modal trabaja en bruto y convierte bruto→neto al guardar, con reparto proporcional y la
// última cuota absorbiendo el residuo → Σ(neto) == costo_unitario EXACTO (invariante preservado).
// Un activo exento o en moneda ≠ local del país no lleva IVA → bruto == neto (conversión identidad).

const HOY = new Date().toISOString().split("T")[0];
// Fase 3c: una cuota está BLOQUEADA (objetivo no editable, no se re-upserta) si tiene pagos aplicados
// en el ledger (aplicadoTarget > 0). Fase 3b: fuente ÚNICA el ledger `pagos_capex` — se retiró el
// término `estado==='pagada'` (el flag ya no es fuente de saldado). El candado duro está en la base
// (trigger); esto es la UX. Un parcial (con eventos) también protege su objetivo.
const esPagada = (c) => Number(c._aplicadoTarget || 0) > 0;

const normalizarFecha = (f) => (f && String(f).includes("T") ? String(f).substring(0, 10) : f || "");

// Acepta dos formas de entrada, sin romper ninguna:
//  · Caja (FlujoCajaV2): prop `entry` (la fila de proyección) → activo_id + razon_social_receptor.
//  · Edificios (modal Inversión): props directas `activoId` + `concepto`.
// En ambos casos el total/moneda salen de activos.costo_unitario (el modal busca el activo por id).
const PlanPagosActivoModal = ({ show, onHide, entry, activoId, concepto, userId, onSaved }) => {
  const resolvedActivoId = activoId ?? entry?.activo_id;
  const resolvedConcepto = concepto ?? entry?.razon_social_receptor ?? "—";

  const [cargando, setCargando] = useState(true);
  const [activo, setActivo] = useState(null); // { costo_unitario, moneda, pais, exento_iva }
  const [cuotas, setCuotas] = useState([]);
  const [numCuotas, setNumCuotas] = useState(4);
  const [frecuencia, setFrecuencia] = useState("mensual");
  const [fechaInicio, setFechaInicio] = useState(HOY);
  const [guardando, setGuardando] = useState(false);
  // Estado destino de la máquina (2c Fase 2): 'plan' | 'fecha_unica' | 'cero'. Se inicializa según el
  // plan cargado (ver efecto de montaje). `fechaUnica` es la fecha del estado FECHA ÚNICA.
  const [modo, setModo] = useState("plan");
  const [fechaUnica, setFechaUnica] = useState(HOY);

  // Al abrir: trae el activo (total + moneda + país) y su plan existente. El modal se monta fresco
  // en cada apertura (render condicional en el padre), así que basta un efecto de montaje.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data, error } = await client
          .from("activos")
          .select("id, costo_unitario, moneda, pais, exento_iva, fecha_planificada")
          .eq("id", resolvedActivoId)
          .single();
        if (error) throw error;
        const plan = await getPlanPagosActivos(resolvedActivoId);
        // Fase 3c: saldo aplicado por cuota desde el subledger → bloquea el objetivo de las que tienen
        // pagos (aplicadoTarget > 0). Batch, una query (10.234). Degradado a Map vacío si falla.
        const saldos = await getSaldosCapexPorTarget(data.pais).catch(() => new Map());
        if (!vivo) return;
        setActivo(data);
        // Las cuotas viven en NETO en DB. Se presentan/editan en BRUTO (mismo grosseo que la fila).
        // `_dbNet` conserva el neto original de cada cuota → base exacta para el reparto al guardar
        // (las pagadas nunca se reescriben, pero su neto cuenta para el residual de las pendientes).
        const aBrutoCuota = (neto) =>
          brutoCapex(Number(neto || 0), { exento_iva: data.exento_iva, moneda: data.moneda, pais: data.pais });
        // Orden de PRESENTACIÓN por (fecha_vencimiento, numero_cuota): el `#` de la tabla es idx+1 =
        // ordinal por posición, decoupled del numero_cuota crudo (append-only). Nunca se muestra crudo.
        const normalizadas = plan
          .map((c) => ({
            ...c,
            _dbNet: Number(c.monto_cuota),
            _aplicadoTarget: saldos.get(claveTargetCapex("cuota_activo", c.id))?.aplicadoTarget || 0,
            monto_cuota: aBrutoCuota(c.monto_cuota),
            fecha_vencimiento: normalizarFecha(c.fecha_vencimiento),
          }))
          .sort(
            (a, b) =>
              String(a.fecha_vencimiento).localeCompare(String(b.fecha_vencimiento)) ||
              (Number(a.numero_cuota) || 0) - (Number(b.numero_cuota) || 0)
          );
        setCuotas(normalizadas);
        // Estado inicial de la máquina: con cuotas → plan; sin cuotas pero con fecha → fecha única;
        // sin cuotas ni fecha → cero. (Un activo con pagos siempre tiene ≥1 cuota → plan.)
        if (normalizadas.length > 0) setModo("plan");
        else if (data.fecha_planificada) setModo("fecha_unica");
        else setModo("cero");
        if (data.fecha_planificada) setFechaUnica(normalizarFecha(data.fecha_planificada));
      } catch (e) {
        toast.error(`Error al cargar el plan: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!resolvedActivoId) return null;

  const totalNeto = Number(activo?.costo_unitario || 0);
  // Condición afecto/exento: la MISMA que usa la fila (brutoCapex/capexLlevaIvaLocal). Un activo
  // exento o en moneda ≠ local del país → sin IVA → total (y cuotas) en bruto == neto.
  const llevaIva = capexLlevaIvaLocal(activo?.moneda, activo?.pais, activo?.exento_iva);
  // El "Monto total" que se reparte = el BRUTO real a pagar (idéntico al de "Movimientos Futuros").
  const total = brutoCapex(totalNeto, { exento_iva: activo?.exento_iva, moneda: activo?.moneda, pais: activo?.pais });
  const monedaActivo = activo?.moneda === "USD" ? "USD" : activo?.pais === "Chile" ? "CLP" : "PEN";
  const simbolo = monedaActivo === "USD" ? "US$" : activo?.pais === "Chile" ? "$" : "S/";
  const fmt = (n) => `${simbolo} ${Number(n || 0).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;

  // Las cuotas (state) están en BRUTO → se validan contra el total bruto (tolerancia ±1 de cuotasUtil
  // absorbe el redondeo del IVA por-cuota vs. sobre-total, ej. 3×3.768.333 = 11.305.001 vs 11.305.000).
  const sumaCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  const cuadra = validarSumaCuotas(cuotas, total);
  // "Virgen" (UX): ninguna cuota tiene pagos aplicados (aplicadoTarget>0). La RPC lo recalcula del
  // ledger de forma autoritativa; acá solo decide qué transiciones se HABILITAN. Un activo con pago
  // siempre tiene ≥1 cuota pagada → no virgen → obligado a plan.
  const esVirgen = !cuotas.some(esPagada);
  const puedeGuardar =
    !cargando &&
    !guardando &&
    ((modo === "plan" && cuotas.length > 0 && cuadra) ||
      (modo === "fecha_unica" && esVirgen && !!fechaUnica) ||
      (modo === "cero" && esVirgen));

  // Generador de conveniencia: prellena las cuotas PENDIENTES sobre el monto restante (total − pagadas),
  // conservando las pagadas. Cada celda queda editable después.
  const handleGenerar = () => {
    const n = Math.trunc(Number(numCuotas));
    if (!Number.isFinite(n) || n < 1) return toast.error("Ingresá al menos 1 cuota");
    if (!fechaInicio) return toast.error("Elegí una fecha de inicio");
    const pagadas = cuotas.filter(esPagada);
    const restante = total - pagadas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
    const nuevas = generarCuotas(restante, n, fechaInicio, frecuencia, true);
    setCuotas([...pagadas, ...nuevas].map((c, i) => ({ ...c, numero_cuota: i + 1 })));
  };

  // Reinicia el plan de un click: descarta TODAS las cuotas pendientes, conservando las pagadas
  // (esPagada, aplicadoTarget>0) intactas. Reemplaza el workaround de borrar fila por fila. Es una
  // operación de STATE (no persiste sola): al Guardar, el guardado selectivo hace el DELETE de las
  // pendientes que estaban en DB (candado de la base rechaza borrar una con pagos, 10.190). Después
  // el usuario genera de nuevo o deja una sola. Cancelar descarta el reinicio (nada se tocó en DB).
  const hayPendientes = cuotas.some((c) => !esPagada(c));
  const handleReiniciar = () => {
    if (!hayPendientes) return;
    setCuotas((prev) => prev.filter(esPagada));
  };

  const handleCambiarCuota = (idx, field, value) => {
    setCuotas((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const handleAgregarFila = () => {
    setCuotas((prev) => [...prev, { numero_cuota: prev.length + 1, monto_cuota: 0, fecha_vencimiento: HOY }]);
  };

  const handleQuitarFila = (idx) => {
    setCuotas((prev) => prev.filter((_, i) => i !== idx));
  };

  // Arma el payload de cuotas NETO para la RPC (solo modo 'plan'). Conversión BRUTO (state) → NETO
  // (DB, monto_cuota): reparto proporcional al bruto de cada cuota pendiente sobre el neto disponible
  // (costo_unitario − neto ya pagado); la ÚLTIMA cuota absorbe el residuo → Σ(neto pendiente) +
  // Σ(neto pagado) == costo_unitario EXACTO. Sin IVA, bruto == neto (identidad). Las pagadas NO se
  // listan (la RPC las conserva). id presente = pendiente existente (update); sin id = nueva (insert).
  const construirCuotasNeto = () => {
    const pendientesState = cuotas.filter((c) => !esPagada(c));
    const netPagadas = cuotas.filter(esPagada).reduce((s, c) => s + Number(c._dbNet || 0), 0);
    const targetNet = totalNeto - netPagadas;
    const brutosPend = pendientesState.map((c) => Number(c.monto_cuota || 0));
    const sumaBrutoPend = brutosPend.reduce((s, b) => s + b, 0) || 1;
    let acumNeto = 0;
    return pendientesState.map((c, i) => {
      const esUltima = i === pendientesState.length - 1;
      const net = esUltima ? targetNet - acumNeto : Math.round((targetNet * brutosPend[i]) / sumaBrutoPend);
      if (!esUltima) acumNeto += net;
      return {
        ...(c.id ? { id: c.id } : {}),
        monto_cuota: net,
        fecha_vencimiento: c.fecha_vencimiento,
      };
    });
  };

  // Guardado ATÓMICO por la RPC (2c Fase 2) según el modo. La RPC valida virgen/Σ=costo/candados y
  // hace todos los writes en una transacción (10.190/10.239); el JS solo arma el payload (bruto→neto).
  const handleGuardar = async () => {
    if (modo === "plan") {
      if (cuotas.length === 0) return toast.error("Agregá al menos una cuota");
      if (!cuadra) return toast.error(`Las cuotas (${fmt(sumaCuotas)}) no suman el total (${fmt(total)})`);
    }
    if (modo === "fecha_unica" && !fechaUnica) return toast.error("Elegí la fecha del pago único");

    setGuardando(true);
    try {
      if (modo === "plan") {
        await guardarPlanPagosActivo({
          activoId: resolvedActivoId,
          estado: "plan",
          cuotas: construirCuotasNeto(),
          usuarioId: userId,
        });
      } else if (modo === "fecha_unica") {
        await guardarPlanPagosActivo({
          activoId: resolvedActivoId,
          estado: "fecha_unica",
          fechaUnica,
          usuarioId: userId,
        });
      } else {
        await guardarPlanPagosActivo({ activoId: resolvedActivoId, estado: "cero", usuarioId: userId });
      }
      toast.success("Plan de pagos guardado");
      onSaved?.();
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Plan de pagos del CapEx</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 12 }}>
          <div>
            <strong>Concepto:</strong> {resolvedConcepto}
          </div>
          <div>
            <strong>Monto total:</strong>{" "}
            {cargando ? "…" : `${fmt(total)} (${monedaActivo}${llevaIva ? ", con IVA" : ""})`}
          </div>
          <div style={{ color: "#888", fontSize: "0.8rem" }}>
            {llevaIva
              ? `El total y las cuotas incluyen IVA (el monto real que sale por caja), en ${monedaActivo}.`
              : `Las cuotas van en la moneda del activo (${monedaActivo}).`}
          </div>
        </div>

        {/* Selector de estado (2c Fase 2). Con pagos ejecutados, solo 'plan' habilitado. */}
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {[
            { key: "plan", label: "Plan de cuotas" },
            { key: "fecha_unica", label: "Fecha única" },
            { key: "cero", label: "Sin proyección" },
          ].map((op) => {
            const bloqueadoPorPago = op.key !== "plan" && !esVirgen;
            return (
              <Button
                key={op.key}
                size="sm"
                variant={modo === op.key ? "primary" : "outline-secondary"}
                onClick={() => setModo(op.key)}
                disabled={cargando || guardando || bloqueadoPorPago}
                title={
                  bloqueadoPorPago
                    ? "El activo tiene pagos ejecutados: no se puede eliminar el plan ni pasar a fecha única/cero"
                    : undefined
                }
              >
                {op.label}
              </Button>
            );
          })}
        </div>
        {!esVirgen && (
          <div style={{ color: "#c62828", fontSize: "0.8rem", marginBottom: 12 }}>
            Este activo tiene pagos ejecutados: solo se puede editar el plan de cuotas (el saldo no pagado); no se puede
            eliminar el plan ni pasar a fecha única/cero. Las cuotas pagadas quedan bloqueadas.
          </div>
        )}

        {modo === "fecha_unica" && (
          <div style={{ padding: "10px 12px", background: "#f8f9fa", borderRadius: 6, marginBottom: 12 }}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>Fecha del pago único</Form.Label>
              <Form.Control
                type="date"
                value={fechaUnica}
                onChange={(e) => setFechaUnica(e.target.value)}
                disabled={guardando}
                style={{ width: 180 }}
              />
            </Form.Group>
            <div style={{ color: "#888", fontSize: "0.8rem", marginTop: 6 }}>
              Proyecta UNA línea por el monto total ({fmt(total)}) en esa fecha. No es un plan de 1 cuota.
            </div>
          </div>
        )}

        {modo === "cero" && (
          <div style={{ color: "#888", fontSize: "0.85rem", marginBottom: 12 }}>
            Sin proyección: el activo no proyectará ningún pago en el Flujo de Caja (sin cuotas ni fecha).
          </div>
        )}

        {modo === "plan" && (
          <>
            {/* Generador de conveniencia */}
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                flexWrap: "wrap",
                padding: "10px 12px",
                background: "#f8f9fa",
                borderRadius: 6,
                marginBottom: 12,
              }}
            >
              <Form.Group>
                <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>N° cuotas</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={numCuotas}
                  onChange={(e) => setNumCuotas(e.target.value)}
                  disabled={guardando}
                  style={{ width: 90 }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>Frecuencia</Form.Label>
                <Form.Select
                  value={frecuencia}
                  onChange={(e) => setFrecuencia(e.target.value)}
                  disabled={guardando}
                  style={{ width: 130 }}
                >
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontSize: "0.8rem", marginBottom: 2 }}>Fecha inicio</Form.Label>
                <Form.Control
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  disabled={guardando}
                  style={{ width: 160 }}
                />
              </Form.Group>
              <Button variant="outline-secondary" size="sm" onClick={handleGenerar} disabled={cargando || guardando}>
                Generar cuotas
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleReiniciar}
                disabled={cargando || guardando || !hayPendientes}
                title="Descarta todas las cuotas pendientes (las pagadas quedan intactas)"
              >
                Reiniciar plan
              </Button>
            </div>

            {/* Tabla editable de cuotas */}
            <Table size="sm" bordered style={{ fontSize: "0.85rem", marginBottom: 8 }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Vencimiento</th>
                  <th>Monto ({monedaActivo})</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {cuotas.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", color: "#888" }}>
                      Sin cuotas. Generá o agregá filas.
                    </td>
                  </tr>
                ) : (
                  cuotas.map((c, idx) => {
                    const bloqueada = esPagada(c);
                    return (
                      <tr key={c.id || `nueva-${idx}`}>
                        <td>{idx + 1}</td>
                        <td>
                          <Form.Control
                            type="date"
                            size="sm"
                            min={HOY}
                            value={normalizarFecha(c.fecha_vencimiento)}
                            onChange={(e) => handleCambiarCuota(idx, "fecha_vencimiento", e.target.value)}
                            disabled={bloqueada || guardando}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            size="sm"
                            value={c.monto_cuota}
                            onChange={(e) => handleCambiarCuota(idx, "monto_cuota", e.target.value)}
                            disabled={bloqueada || guardando}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {bloqueada ? (
                            <span style={{ color: "#888", fontSize: "0.75rem" }}>
                              {computarSaldoCapex(c._dbNet, c._aplicadoTarget).saldada ? "pagada" : "con pago"}
                            </span>
                          ) : (
                            <Button
                              variant="link"
                              size="sm"
                              style={{ color: "#c62828", padding: 0, textDecoration: "none" }}
                              onClick={() => handleQuitarFila(idx)}
                              disabled={guardando}
                            >
                              ✕
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleAgregarFila}
                disabled={cargando || guardando}
              >
                + Agregar cuota
              </Button>
              <div style={{ fontSize: "0.9rem", color: cuadra ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                Σ cuotas: {fmt(sumaCuotas)} / {fmt(total)}
                {!cuadra && <span style={{ marginLeft: 8, fontWeight: 400 }}>(no cuadra)</span>}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={!puedeGuardar}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanPagosActivoModal;
